// App.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase"; // Adjust path if needed
import { Modal, Button, Form } from "react-bootstrap";
import { ChordProParser, HtmlDivFormatter, TextFormatter, Key } from "chordsheetjs";
import "./App.css";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Toast, ToastContainer } from "react-bootstrap";
import SaveOfflineButton from "./components/SaveOfflineButton";
import { useOffline } from "./offline/offlineContext";
import { loadOffline } from "./offline/offlineManager";
import db from "./offline/db";

function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefill = params.get("prefillEmail");
    if (prefill) {
      setEmail(prefill);
    }
  }, []);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState("");
  const [songs, setSongs] = useState([]);
  const [bands, setBands] = useState([]);
  const [selectedBand, setSelectedBand] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [chordKey, setChordKey] = useState("");
  const [fontSize, setFontSize] = useState(() => {
    const savedFontSize = localStorage.getItem("chordProFontSize");
    return savedFontSize ? parseFloat(savedFontSize) : 16;
  });
  const [showVideo, setShowVideo] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const { isOffline } = useOffline();
  useEffect(() => {
    const initSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        setUser(data.session.user);
        fetchUsername(data.session.user.id);
      } else {
        setUser(null);
      }
    };

    initSession();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const authUser = session?.user ?? null;
      setUser(authUser);
      if (authUser) fetchUsername(authUser.id);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const fetchUsername = async (uid) => {
    const { data, error } = await supabase.from("users").select("username").eq("supabase_uid", uid).single();

    if (error) {
      console.error("Error fetching username:", error);
    } else {
      setUsername(data?.username || "");
    }
  };
  useEffect(() => {
    if (selectedBand && user) {
      const fetchBandDefault = async () => {
        const { data, error } = await supabase.from("bands").select("default_service_title").eq("id", selectedBand).single();

        if (error) {
          setError("Failed to fetch band details: " + error.message);
        } else {
          setNewServiceTitle(data?.default_service_title || ""); // Prefill with default or empty
        }
      };
      fetchBandDefault();
      fetchServices();
      checkIsLeader(selectedBand).then(setIsLeader);
    }
  }, [selectedBand]);
  useEffect(() => {
    if (user && selectedBand) {
      const check = async () => {
        setLeaderCheckDone(false); // start loading state
        const leader = await checkIsLeader(selectedBand);
        setIsLeader(leader);
        setLeaderCheckDone(true); // finished checking
      };
      check();
    } else {
      setIsLeader(false);
      setLeaderCheckDone(true);
    }
  }, [user, selectedBand]);

  const [isLeader, setIsLeader] = useState(null); // null = loading
  const [leaderCheckDone, setLeaderCheckDone] = useState(false);
  const [newServiceTitle, setNewServiceTitle] = useState("");
  const [newServiceDate, setNewServiceDate] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [allSongs, setAllSongs] = useState([]);
  const [songSearch, setSongSearch] = useState("");
  const [showSongForm, setShowSongForm] = useState(false);
  const [editingSong, setEditingSong] = useState(null); // null for create, song object for edit
  const [songFormData, setSongFormData] = useState({
    title: "",
    artist: "",
    skey: "",
    url: "",
    chordpro: "",
    spotify: "",
    track: "",
  });
  const [rememberMe, setRememberMe] = useState(true);

  // Notes canvas (new implementation)
  const canvasRef = useRef(null); // override or reuse the existing one if present
  const ctxRef = useRef(null);
  const isDrawing = useRef(false);
  const [isEditing, setIsEditing] = useState(false); // edit mode: drawing enabled
  const [notesVisibleOn, setNotesVisibleOn] = useState(true); // visible by default
  // helper key; used to store image data per song
  const notesKey = useMemo(() => {
    if (!selectedSong) return null;
    const id = selectedSong.id || selectedSong.songid || selectedSong.songId || selectedSong.ID;
    return id ? `chord_notes_${id}` : null;
  }, [selectedSong?.id, selectedSong?.songid, selectedSong?.songId, selectedSong?.ID]);

  const checkIsLeader = async (bandId) => {
    if (!user?.id || !bandId) return false;

    const { data, error: err } = await supabase.from("user_bands").select("role").eq("user_id", user.id).eq("band_id", bandId).single();

    if (err) {
      // PGRST116 = no row → not in band
      if (err.code === "PGRST116") return false;
      console.error("checkIsLeader error:", err);
      return false;
    }

    return data?.role === "leader";
  };
  useEffect(() => {
    if (!user) return;
    fetchBands();
  }, [user]);

  const fetchBands = async () => {
    const { data, error: err } = await supabase
      .from("user_bands")
      .select(
        `
        band_id,
        role,
        bands!user_bands_band_id_fkey (
          id,
          name,
          description
        )
      `,
      )
      .eq("user_id", user.id);
    if (err) {
      setError("Failed to fetch bands: " + err.message);
      console.error("fetchBands error:", err);
      return;
    }

    const bandList = data.map((row) => ({
      ID: row.bands.id,
      name: row.bands.name,
      description: row.bands.description,
      role: row.role,
    }));

    setBands(bandList);
    if (bandList.length > 0) {
      setSelectedBand(bandList[0].ID);
    }
  };
  const fetchServices = async () => {
    if (!selectedBand) return;

    const today = new Date().toISOString().split("T")[0];

    const { data, error: err } = await supabase.from("services").select("id, servicetitle, sdate").eq("band_id", selectedBand).order("sdate", { ascending: false }).limit(20);

    if (err) {
      setError("Failed to load services: " + err.message);
      return;
    }

    if (!data || data.length === 0) {
      setServices([]);
      setSelectedService("");
      return;
    }

    setServices(data);

    const upcoming = data.find((s) => s.sdate >= today);
    if (upcoming) {
      setSelectedService(upcoming.id);
      return;
    }

    // 2. Fallback: most recent past (data[0] = newest)
    setSelectedService(data[0].id);
  };

  const createService = async () => {
    if (!isLeader) {
      setError("Only leaders can create services");
      return;
    }

    if (!newServiceDate || !newServiceTitle.trim()) {
      setError("Please enter a date and title");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("services")
        .insert({
          band_id: selectedBand,
          sdate: newServiceDate,
          servicetitle: newServiceTitle.trim(),
        })
        .select(); // ← Returns array: data: [newRow]

      if (error) throw error;
      if (!data || data.length === 0) throw new Error("No data returned from insert");

      const newService = data[0]; // ← Extract first (and only) row

      // Refresh services list
      await fetchServices();

      // Auto-select the new service
      setSelectedService(newService.ID.toString());

      // Reset form
      setNewServiceDate("");
      setNewServiceTitle("");
      setShowEditModal(false);
    } catch (err) {
      console.error("Create service error:", err);
      setError("Failed to create service: " + err.message);
    }
  };

  const fetchSongs = async () => {
    if (isOffline) {
      const { service, songs } = await loadOffline();
      if (songs.length) {
        setServices(service);
        setSongs(songs);
      } else {
        setError("No offline setlist found.");
      }
      return;
    }
    if (!selectedService && selectedBand) {
      const { data: latest } = await supabase.from("services").select("id").eq("band_id", selectedBand).lte("sdate", new Date().toISOString().split("T")[0]).order("sdate", { ascending: false }).limit(1).single();

      if (latest) {
        setSelectedService(latest.id);
      } else {
        setSongs([]);
        return;
      }
    }

    const { data, error: err } = await supabase
      .from("servicessongs")
      .select(
        `
        id,
        order,
        custom_key,
        songid,
        songs!inner (
          id,
          title,
          artist,
          skey,
          url,
          chordpro,
          spotify,
          track
        )
      `,
      )
      .eq("serviceid", selectedService)
      .order("order", { ascending: true });

    if (err) {
      setError("Failed to load songs: " + err.message);
      return;
    }

    setSongs(
      data.map((item) => {
        return {
          ss_id: item.id, // ← servicessongs.id (e.g. 768)
          songid: String(item.songid),
          title: item.songs.title,
          artist: item.songs.artist,
          skey: item.custom_key || item.songs.skey,
          url: item.songs.url,
          chordpro: item.songs.chordpro,
          spotify: item.songs.spotify,
          track: item.songs.track,
          order: item.order,
          // DO NOT include songs.id or ID
        };
      }),
    );
  };
  const fetchAllSongs = async () => {
    if (!selectedBand) return;

    const { data, error: err } = await supabase.rpc("get_band_song_library", { p_band_id: selectedBand });

    if (err) {
      setError("Failed to load song library: " + err.message);
      return;
    }

    if (!data || data.length === 0) {
      setAllSongs([]);
      return;
    }

    const formatted = data.map((row) => ({
      id: row.song_id, // ← USE `id` (lowercase)
      title: row.title,
      artist: row.artist,
      skey: row.skey,
      url: row.url,
      chordpro: row.chordpro,
      spotify: row.spotify,
      track: row.track,
    }));

    setAllSongs(formatted);
  };

  const addSongToSetlist = async (songId) => {
    if (!selectedService || !songId) {
      console.warn("Missing service or song ID");
      return;
    }

    const { error } = await supabase.from("servicessongs").insert({
      serviceid: selectedService,
      songid: songId,
      order: songs.length + 1, // Append to end
    });

    if (error) {
      console.error("Insert error:", error);
      setError("Failed to add song: " + error.message);
      return;
    }

    await fetchSongs(); // Refresh setlist
    setError(""); // Clear
    setSongSearch("");
    setAllSongs((prev) => [...prev]); // Trigger re-render showing all songs again
  };

  // ===========================
  // AUTO SCROLLING FEATURE
  // ===========================
  const scrollActive = useRef(false);
  const scrollTimer = useRef(null);
  const durationRef = useRef(300000); // 5 minutes
  const wasScrolling = useRef(false);
  const resumeTimeout = useRef(null);

  const startScroll = () => {
    const play = document.querySelector(".startscroll");
    const stop = document.querySelector(".stopscroll");
    const container = document.querySelector(".modal-body");

    if (!container) return;

    const totalHeight = container.scrollHeight - container.clientHeight;
    const startY = container.scrollTop;
    const remaining = totalHeight - startY;
    const duration = (1 - startY / totalHeight) * durationRef.current || durationRef.current;

    if (remaining <= 0) return;

    scrollActive.current = true;
    if (play && stop) {
      play.style.display = "none";
      stop.style.display = "inline-block";
    }

    const startTime = performance.now();

    const step = (now) => {
      if (!scrollActive.current) return;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      container.scrollTop = startY + remaining * progress;

      if (progress < 1) {
        scrollTimer.current = requestAnimationFrame(step);
      } else {
        stopScroll();
      }
    };

    scrollTimer.current = requestAnimationFrame(step);
  };

  const stopScroll = () => {
    const play = document.querySelector(".startscroll");
    const stop = document.querySelector(".stopscroll");
    if (scrollTimer.current) cancelAnimationFrame(scrollTimer.current);
    scrollActive.current = false;
    if (play && stop) {
      play.style.display = "inline-block";
      stop.style.display = "none";
    }
  };

  useEffect(() => {
    const container = document.querySelector(".modal-body");
    if (!container) {
      return;
    }

    const handleManual = (e) => {
      if (e.target.closest("#toggleNavButton, #nextbutton, #ytbutton, #lockButton, #toggleNotesButton, .startscroll, .stopscroll")) {
        // Stop autoscroll immediately so the button tap is not swallowed
        if (scrollActive.current) stopScroll();
        // Let the button click continue normally
        return;
      }

      if (scrollActive.current) {
        stopScroll();
        clearTimeout(resumeTimeout.current);

        // 🕒 After user stops interacting for 2 seconds, resume auto-scroll
        resumeTimeout.current = setTimeout(() => {
          startScroll();
        }, 2000);
      }
    };
    // Extra patch for iOS/Android: ensure first tap on buttons triggers immediately
    const handleImmediateButtonTap = (e) => {
      const button = e.target.closest("#toggleNavButton, #nextbutton, #ytbutton, #lockButton, #toggleNotesButton, .startscroll, .stopscroll");

      if (scrollActive.current && button) {
        stopScroll(); // stop autoscroll immediately

        // Force the button's click to fire immediately on this touchstart
        // because otherwise Safari will swallow the tap as "scroll stop"
        button.click();

        // Prevent this touch from being interpreted as a scroll gesture
        e.preventDefault();
      }
    };

    container.addEventListener("touchstart", handleImmediateButtonTap, {
      passive: false,
    });

    container.addEventListener("touchstart", handleManual);
    container.addEventListener("wheel", handleManual);

    return () => {
      clearTimeout(resumeTimeout.current);
      container.removeEventListener("touchstart", handleManual);
      container.removeEventListener("wheel", handleManual);
      container.removeEventListener("touchstart", handleImmediateButtonTap);
    };
  }, [showModal]);

  useEffect(() => {
    if (showSongForm) {
      if (editingSong) {
        setSongFormData({
          title: editingSong.title || "",
          artist: editingSong.artist || "",
          skey: editingSong.skey || "",
          url: editingSong.url || "",
          chordpro: editingSong.chordpro || "",
          spotify: editingSong.spotify || "",
          track: editingSong.track || editingSong.url || "",
        });
      } else {
        setSongFormData({
          title: "",
          artist: "",
          skey: "",
          url: "",
          chordpro: "",
          spotify: "",
          track: "",
        });
      }
    }
  }, [showSongForm, editingSong]);

  const openSongForm = (song = null) => {
    setEditingSong(song);
    setShowSongForm(true);
  };

  const handleSongSubmit = async (e) => {
    e.preventDefault();

    // Leader-only check
    if (isLeader === false) {
      setError("Only leaders can edit or create songs");
      return;
    }

    try {
      const data = {
        title: songFormData.title,
        artist: songFormData.artist,
        skey: songFormData.skey,
        url: songFormData.url,
        chordpro: songFormData.chordpro,
        spotify: songFormData.spotify,
        track: songFormData.track || songFormData.url,
        band_id: selectedBand,
      };

      let error;

      if (editingSong) {
        const songId = editingSong?.songid || editingSong?.id || editingSong?.ID;
        ({ error } = await supabase.from("songs").update(data).eq("id", songId));

        // Update selected song if open
        if (selectedSong && (selectedSong.id === songId || selectedSong.songid === songId)) {
          setSelectedSong({ ...selectedSong, ...data });
        }
      } else {
        ({ error } = await supabase.from("songs").insert(data).select());
      }

      if (error) throw error;

      setShowSongForm(false);
      setSongFormData({
        title: "",
        artist: "",
        skey: "",
        url: "",
        chordpro: "",
        spotify: "",
        track: "",
      });
      setEditingSong(null);

      await fetchAllSongs();
      await fetchSongs();
    } catch (err) {
      setError("Failed to save song: " + err.message);
    }
  };
  useEffect(() => {
    if (selectedSong?.skey && chordKey !== selectedSong.skey) {
      setChordKey(selectedSong.skey);
    }
  }, [selectedSong?.skey]);
  useEffect(() => {
    if (selectedSong) {
      stopScroll(); // Stop auto-scroll on any song change

      const match = /\{duration:(\d+)\}/.exec(selectedSong.chordpro);
      durationRef.current = match ? parseInt(match[1], 10) * 1000 : 300000;

      const container = document.querySelector(".modal-body");
      if (container) {
        setTimeout(() => {
          container.scrollTop = 0;
        }, 0); // Ensure reset after render queue
      }
    }
  }, [selectedSong]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    // Load initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user && selectedBand && selectedService) {
      fetchSongs();
    }
  }, [user, selectedBand, selectedService]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const nav = document.getElementById("songnav");
      const toggleButton = document.getElementById("toggleNavButton");
      if (nav && nav.style.display !== "none" && !nav.contains(e.target) && (!toggleButton || !toggleButton.contains(e.target))) {
        toggleNav();
      }
    };

    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("contextmenu", (e) => {
      if (e.target.classList.contains("notes-canvas")) e.preventDefault();
    });
    return () => {
      document.removeEventListener("click", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // Supabase doesn't need explicit persistence—it's handled via storage
      const { data, error: err } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      setUser(data.user);
      // Optional: If !rememberMe, you could implement session-only by clearing on window close, but skip for now
    } catch (err) {
      setError(err.message || "Login failed");
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setSelectedBand("");
      setSelectedService("");
      // Clear other states as needed
    } catch (err) {
      setError("Logout failed: " + err.message);
    }
  };

  const openChordModal = async (song) => {
    try {
      // --- 1️⃣ Save current canvas before switching songs ---
      try {
        const currentKey = notesKey; // notesKey is based on current selectedSong
        if (canvasRef.current && currentKey) {
          const dataUrl = canvasRef.current.toDataURL();
          if (dataUrl) localStorage.setItem(currentKey, dataUrl);
        }
      } catch (saveErr) {
        console.warn("Failed to autosave canvas before switching song:", saveErr);
      }

      // --- 2️⃣ Turn off editing when switching songs ---
      if (isEditing) setIsEditing(false);

      // --- 3️⃣ Normalize song ID ---
      const songId = song?.songid || song?.id || song?.ID;
      if (!songId) throw new Error("Invalid song object (no id)");

      // --- 4️⃣ OFFLINE FALLBACK ---
      if (isOffline) {
        console.log("🔌 Offline mode: loading song from IndexedDB cache...");

        const cachedSong = await db.offlineSongs.where("songid").equals(Number(songId)).first();

        if (!cachedSong) {
          throw new Error("Song not found in offline cache.");
        }

        // Set selected song and related UI state
        setSelectedSong({
          ...cachedSong,
          songid: cachedSong.songid,
          custom_key: song.skey || song.custom_key || cachedSong.skey,
          order: song.order,
        });

        setChordKey(cachedSong.skey || song.skey || "A");
        setShowVideo(false);
        setVideoLoaded(false);
        setShowModal(true);

        console.log("✅ Loaded cached song:", cachedSong.title);
        return; // stop here — skip Supabase call
      }

      // --- 5️⃣ ONLINE MODE ---
      const { data, error: err } = await supabase.from("songs").select("*").eq("id", songId).eq("band_id", selectedBand).single();

      if (err) throw err;

      console.log("Loaded new song data:", data);

      // --- 6️⃣ Set selected song and UI states ---
      setSelectedSong({
        ...data,
        songid: data.id, // normalize
        custom_key: song.skey || song.custom_key || data.skey,
        order: song.order,
      });

      setChordKey(data.skey || song.skey || "A");
      setShowVideo(false);
      setVideoLoaded(false);
      setShowModal(true);
    } catch (err) {
      console.error("Error opening song:", err);
      setError(`Failed to open song ${song?.title || "(unknown)"}: ${err.message}`);
    }
  };

  const closeChordModal = () => {
    try {
      // Save current canvas for the currently selected song
      const key = notesKey;
      if (canvasRef.current && key) {
        try {
          console.log("💾 Saving and closing chord modal. Current notesKey:", notesKey);

          const data = canvasRef.current.toDataURL();
          if (data) localStorage.setItem(key, data);
        } catch (e) {
          console.warn("Failed to save canvas on modal close:", e);
        }
      }
    } finally {
      // Ensure editing mode is turned off
      setIsEditing(false);
      setShowModal(false);
      console.log("❌ Modal closed, editing off.");
    }
  };
  const handleNextSong = () => {
    if (!selectedSong || !songs.length) {
      setError("No song selected or setlist is empty");
      return;
    }
    const currentIndex = songs.findIndex((song) => String(song.songid) === String(selectedSong.songid || selectedSong.id || selectedSong.ID));
    if (currentIndex === -1) {
      setError("Current song not found in setlist");
      return;
    }
    const nextIndex = (currentIndex + 1) % songs.length; // Wrap around using modulo
    const nextSong = songs[nextIndex];
    openChordModal(nextSong);
  };

  const toFlatNotation = (chord) => {
    const SHARP_TO_FLAT = {
      "A#": "Bb",
      "C#": "Db",
      "D#": "Eb",
      "F#": "Gb",
      "G#": "Ab",
    };

    const parsePart = (part) => {
      const match = part.match(/^([A-G])(#|b)?(.*?)$/);
      if (!match) return part;
      const root = match[1];
      const acc = match[2] || "";
      const suffix = match[3] || "";
      let note = root + acc;
      if (acc === "#") {
        note = SHARP_TO_FLAT[note] || note;
      }
      // Preserve flats (e.g., "Bb") and natural notes (e.g., "C")
      return note + suffix;
    };

    // Split and process slash chords (e.g., "G#/C" → ["G#", "C"] → ["Ab", "C"])
    const parts = chord.split("/");
    const convertedParts = parts.map(parsePart);
    return convertedParts.join("/");
  };

  const transposeChordPro = (chordPro, newKey, skey) => {
    try {
      const parser = new ChordProParser();
      const song = parser.parse(chordPro);

      const originalKey = (song.key || skey || "C").replace(/[^A-G#b]/gi, "");
      const targetKey = (newKey || originalKey).replace(/[^A-G#b]/gi, "");

      const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
      const FLAT_EQUIVS = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };

      const normalize = (k) => FLAT_EQUIVS[k] || k;

      const orig = normalize(originalKey);
      const targ = normalize(targetKey);

      const interval = (NOTES.indexOf(targ) - NOTES.indexOf(orig) + 12) % 12;

      const transposedSong = song.transpose(interval);

      const formatter = new HtmlDivFormatter();
      let htmlOutput = formatter.format(transposedSong);

      // Only convert sharps to flats for flat-preferring keys
      const flatKeys = ["F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb", "C"];
      if (flatKeys.includes(targetKey)) {
        htmlOutput = htmlOutput.replace(/<div class="chord">([^<]+)<\/div>/gi, (match, fullChord) => {
          const converted = toFlatNotation(fullChord);
          return `<div class="chord">${converted}</div>`;
        });
      }

      // Fix trailing spaces in lyrics
      htmlOutput = htmlOutput.replace(/<div class="lyrics">([^<]*?)<\/div>/g, (match, text) => {
        return `<div class="lyrics">${text.replace(/ $/, "\u00A0")}</div>`;
      });

      return htmlOutput;
    } catch (err) {
      console.error("ChordPro parsing error:", err);
      return chordPro.replace(/\n/g, "<br>");
    }
  };

  const handleKeyChange = (e) => {
    setChordKey(e.target.value);
  };

  const handleTextSize = (increase) => {
    setFontSize((prev) => {
      const newSize = increase ? prev * 1.2 : (prev * 5) / 6;
      localStorage.setItem("chordProFontSize", newSize);
      return newSize;
    });
  };

  const toggleNav = () => {
    const nav = document.getElementById("songnav");
    if (nav) {
      nav.style.display = nav.style.display === "none" ? "flex" : "none";
    }
  };

  const toggleVid = (url) => {
    const iframe = document.getElementById("yt-iframe");
    if (iframe) {
      iframe.style.display = iframe.style.display === "none" ? "block" : "none";
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !selectedSong || !showModal) return;

    let cancelled = false; // 🧩 prevents cross-song overlap
    setIsEditing(false);

    console.log("🎨 Canvas effect triggered. notesKey:", notesKey, "song:", selectedSong?.title);
    console.log(selectedSong);
    const container = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "yellow";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctxRef.current = ctx;

    const resize = () => {
      if (cancelled) return;
      canvas.width = container.clientWidth;
      canvas.height = container.scrollHeight;
      ctx.strokeStyle = "yellow";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    };

    const restoreSavedImage = () => {
      if (cancelled) return;
      const saved = notesKey ? localStorage.getItem(notesKey) : null;
      if (saved) {
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          console.log("🖼️ Restored notes for", selectedSong?.title, notesKey);
          console.log("📏 Canvas size:", canvas.width, "x", canvas.height);
        };
        img.src = saved;
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        console.log("🧹 No saved notes found for", selectedSong?.title);
      }
    };

    // Initial resize + restore
    resize();
    restoreSavedImage();

    // Observe resizing
    const observer = new ResizeObserver(() => {
      if (!cancelled) {
        resize();
        restoreSavedImage();
      }
    });
    observer.observe(container);

    // Cleanup
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [selectedSong, showModal, notesKey]);

  // run each time modal opens or song changes
  // Convert page coordinates to canvas coordinates
  const getCanvasPosFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // start drawing
  const startDraw = (e) => {
    if (!isEditing) return;
    e.preventDefault();
    isDrawing.current = true;
    const pos = getCanvasPosFromEvent(e);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  // drawing
  const draw = (e) => {
    if (!isEditing || !isDrawing.current) return;
    e.preventDefault();
    const pos = getCanvasPosFromEvent(e);
    const ctx = ctxRef.current;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  // end drawing
  const endDraw = (e) => {
    if (!isEditing) return;
    isDrawing.current = false;
    const ctx = ctxRef.current;
    ctx.closePath();
    // persist drawing as PNG
    const key = notesKey;
    if (key && canvasRef.current) {
      try {
        const data = canvasRef.current.toDataURL();
        localStorage.setItem(key, data);
      } catch (err) {
        console.warn("Failed to save notes to localStorage:", err);
      }
    }
  };

  // toggle edit mode (enable/disable drawing)
  const toggleEditMode = () => {
    setIsEditing((prev) => {
      const next = !prev;
      // when entering edit mode, pause autoscroll
      if (next && scrollActive.current) {
        stopScroll();
      }
      // while editing, prevent touch scrolling by adding pointer-capture on canvas via CSS
      return next;
    });
  };

  // clear notes for this song
  const clearNotesForSong = () => {
    const key = notesKey;
    if (canvasRef.current && ctxRef.current) {
      ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    if (key) localStorage.removeItem(key);
  };

  const transposedHtml = useMemo(() => {
    if (!selectedSong || !selectedSong.chordpro) return "";
    return transposeChordPro(selectedSong.chordpro, chordKey, selectedSong.skey);
  }, [selectedSong?.id, selectedSong?.chordpro, selectedSong?.skey, selectedSong?.url, chordKey]);

  return (
    <div className="fade-in">
      <div className="App">
        {authLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : !user ? (
          <div className="login-container">
            <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
              <div className="card shadow-sm p-4" style={{ maxWidth: "400px", width: "100%" }}>
                <div className="text-center mb-4">
                  <h2 className="fw-bold">✝️ CrossTracks 🎵</h2>
                </div>
                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required className="rounded-pill" />
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label>Password</Form.Label>
                    <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="rounded-pill" />
                    <Form.Check type="checkbox" label="Remember me" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="mb-3" />
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100 rounded-pill">
                    Worship!
                  </Button>
                </Form>
              </div>
            </div>
            <ToastContainer position="top-end" className="p-3">
              <Toast onClose={() => setError("")} delay={3000} autohide show={!!error} bg="danger">
                <Toast.Header>
                  <strong className="me-auto">Error</strong>
                </Toast.Header>
                <Toast.Body>{error}</Toast.Body>
              </Toast>
            </ToastContainer>
          </div>
        ) : (
          <div>
            <nav className="navbar navbar-expand-lg navbar-dark bg-dark shadow-sm">
              <div className="container-fluid d-flex justify-content-between align-items-center flex-wrap gap-2 py-2">
                {/* Left side: Band & Service selectors */}
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex flex-column">
                    <select id="bandSelect" className="form-select form-select-sm bg-secondary text-light border-0" value={selectedBand} onChange={(e) => setSelectedBand(e.target.value)} style={{ minWidth: "180px" }}>
                      {bands.map((band) => (
                        <option key={band.id} value={band.id}>
                          {band.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right side: Welcome + Logout */}
                <div className="d-flex align-items-center text-light gap-3">
                  <small>
                    Welcome, <strong>{username || user.email}</strong>
                  </small>
                  <Button variant="outline-light" size="sm" onClick={handleLogout}>
                    Logout
                  </Button>
                </div>
              </div>
            </nav>

            {/* Leader-only controls */}
            {!leaderCheckDone ? null : isLeader ? (
              <div className="container my-4">
                <div className="card bg-light shadow-sm p-3">
                  <h5 className="text-center mb-3 text-dark fw-semibold">Create New Service</h5>
                  <div className="row g-3 align-items-center justify-content-center">
                    <div className="col-12 col-md-4">
                      <input type="text" className="form-control" placeholder="Service Title" value={newServiceTitle} onChange={(e) => setNewServiceTitle(e.target.value)} />
                    </div>
                    <div className="col-12 col-md-3">
                      <input type="date" className="form-control" value={newServiceDate} onChange={(e) => setNewServiceDate(e.target.value)} />
                    </div>
                    <div className="col-12 col-md-2">
                      <Button variant="primary" className="w-100" onClick={createService} disabled={!newServiceDate || !newServiceTitle.trim()}>
                        Create
                      </Button>
                    </div>
                    <div className="col-12 col-md-2">
                      <Button
                        variant="secondary"
                        className="w-100"
                        onClick={async () => {
                          setShowEditModal(true);
                          await fetchAllSongs();
                        }}
                        disabled={!selectedService}
                      >
                        Edit Setlist
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl">
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: "1.5em", whiteSpace: "normal" }}>
                  Edit Setlist
                  {services.find((s) => s.id === parseInt(selectedService))?.servicetitle}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="edit-modal-body" style={{ display: "flex", height: "500px", overflow: "auto" }}>
                <DragDropContext
                  onDragEnd={async (result) => {
                    if (!result.destination) return;

                    // Optimistic update
                    const items = Array.from(songs);
                    const [reorderedItem] = items.splice(result.source.index, 1);
                    items.splice(result.destination.index, 0, reorderedItem);
                    setSongs(items);

                    const isLeader = await checkIsLeader(selectedBand);
                    if (!isLeader) {
                      setError("Only leaders can reorder songs");
                      await fetchSongs();
                      return;
                    }

                    const orderUpdates = items.map((song, index) => ({
                      songid: song.songid,
                      order: index + 1,
                    }));

                    try {
                      const updatePromises = orderUpdates.map(({ songid, order }) => supabase.from("servicessongs").update({ order }).eq("serviceid", selectedService).eq("songid", songid));

                      const results = await Promise.all(updatePromises);
                      const errors = results.filter((r) => r.error);
                      if (errors.length > 0) throw errors[0].error;
                    } catch (err) {
                      setError("Failed to save order: " + err.message);
                      await fetchSongs(); // rollback
                    }
                  }}
                >
                  <Droppable droppableId="setlist">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="setlist-panel"
                        style={{
                          width: "50%",
                          borderRight: "1px solid #ddd",
                          padding: "10px",
                        }}
                      >
                        {songs.map((song, index) => (
                          <Draggable key={song.ss_id} draggableId={String(song.ss_id)} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="draggable-item"
                                style={{
                                  ...provided.draggableProps.style,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "8px",
                                  margin: "4px 0",
                                  background: "#f8f9fa",
                                  borderRadius: "4px",
                                }}
                              >
                                <span className="drag-handle" style={{ cursor: "grab" }}>
                                  ☰
                                </span>
                                <div style={{ flex: "1 1 auto", display: "flex", alignItems: "center", gap: "8px" }}>
                                  <span>{song.title}</span>
                                  <label style={{ marginLeft: "auto" }}>Key</label>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: "80px" }}
                                    value={song.skey}
                                    onChange={async (e) => {
                                      const newKey = e.target.value;
                                      if (!isLeader) {
                                        setError("Only leaders can change song keys");
                                        return;
                                      }

                                      const { error } = await supabase.from("songs").update({ skey: newKey }).eq("id", song.songid);

                                      if (error) {
                                        console.error("Key update failed:", error);
                                        setError("Failed to update key: " + error.message);
                                      } else {
                                        // Optimistic local update
                                        setSongs((prev) => prev.map((s) => (s.ss_id === song.ss_id ? { ...s, skey: newKey, custom_key: newKey } : s)));
                                      }
                                    }}
                                  >
                                    {["Ab", "A", "Bb", "B", "C", "D", "Eb", "E", "F", "G"].map((key) => (
                                      <option key={key} value={key}>
                                        {key}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    if (!isLeader) {
                                      setError("Only leaders can remove songs");
                                      return;
                                    }

                                    const servicessongId = song.ss_id;

                                    if (!servicessongId) {
                                      setError("Cannot delete: missing servicessong ID");
                                      return;
                                    }

                                    const { error } = await supabase.from("servicessongs").delete().eq("id", servicessongId);

                                    if (error) {
                                      console.error("Delete error:", error);
                                      setError("Remove failed: " + error.message);
                                    } else {
                                      await fetchSongs();
                                    }
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <div className="library-panel" style={{ width: "50%", padding: "10px", overflow: "auto" }}>
                  <h5>Song Library</h5>

                  {isLeader === null ? (
                    <div className="text-center my-4 text-muted">
                      <small>Loading permissions...</small>
                    </div>
                  ) : isLeader ? (
                    <Button variant="primary" onClick={() => openSongForm()} className="mb-3">
                      Add New Song
                    </Button>
                  ) : null}

                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={songSearch}
                    onChange={(e) => setSongSearch(e.target.value)}
                    style={{
                      width: "100%",
                      marginBottom: "10px",
                      padding: "5px",
                    }}
                  />

                  {allSongs
                    .filter((song) => song.title.toLowerCase().includes(songSearch.toLowerCase()))
                    .map((song) => (
                      <div
                        key={song.songid}
                        className="d-flex justify-content-between align-items-start mb-2"
                        style={{
                          borderBottom: "1px solid #ddd",
                          paddingBottom: "6px",
                          wordBreak: "break-word",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            flex: "1 1 auto",
                            marginRight: "8px",
                            whiteSpace: "normal",
                          }}
                        >
                          {song.title} ({song.skey})
                        </span>
                        <div style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                          {isLeader === null ? (
                            <div className="text-center my-4 text-muted">
                              <small>Loading permissions...</small>
                            </div>
                          ) : isLeader ? (
                            <Button variant="warning" size="sm" className="me-2" title="Edit Song" onClick={() => openSongForm(song)}>
                              ✎
                            </Button>
                          ) : null}
                          <Button
                            variant="success"
                            size="sm"
                            title="Add to Setlist"
                            onClick={async () => {
                              await addSongToSetlist(song.id); // Reuse function
                            }}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </Modal.Body>
            </Modal>
            <div className="album py-5">
              <div className="container">
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <h2 className="me-2 mb-0">Setlist</h2>
                  <select id="serviceSelect" className="form-select form-select-sm border-0 title-select" value={selectedService} onChange={(e) => setSelectedService(e.target.value)} style={{ minWidth: "220px" }}>
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.servicetitle} ({new Date(`${service.sdate}T00:00:00`).toLocaleDateString(undefined, { timeZone: "UTC" })})
                      </option>
                    ))}
                  </select>
                  <SaveOfflineButton selectedService={selectedService} selectedBand={selectedBand} setError={setError} />
                </div>
                <div className="row">
                  {songs.length > 0 ? (
                    songs.map((song) => (
                      <div className="col-md-4" key={song.id}>
                        <div className="card mb-4 shadow-sm">
                          {/* Temporary disable YouTube preview until redesign */}
                          {/*
                        {song.url ? (
                          <img
                            src={`https://img.youtube.com/vi/${song.url}/hqdefault.jpg`}
                            alt={song.title}
                            className="card-img-top"
                            onClick={() => window.open(`https://www.youtube.com/watch?v=${song.url}`, '_blank')}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <div className="card-img-top bg-secondary text-white text-center pt-5">No Video</div>
                        )}
                        */}
                          <div className="card-body">
                            <div className="card-title d-flex align-items-center flex-wrap">
                              <span className="order-circle">{song.order}</span>
                              <strong className="ms-2">{song.title}</strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="btn-group">
                                {song.url && (
                                  <a href={`https://www.youtube.com/watch?v=${song.url}`} target="_blank" rel="noopener noreferrer">
                                    <button type="button" className="btn btn-sm btn-outline-secondary">
                                      View on YouTube
                                    </button>
                                  </a>
                                )}
                              </div>
                              <button className="btn btn-sm btn-outline-dark" onClick={() => openChordModal(song)}>
                                Chords ({song.skey})
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center">No songs found for this band.</p>
                  )}
                </div>
              </div>
            </div>
            <Modal show={showModal} onHide={closeChordModal} fullscreen={true}>
              <Modal.Body
                style={{
                  backgroundColor: "black",
                  color: "white",
                  overflow: "auto",
                }}
              >
                {selectedSong && (
                  <div>
                    <div
                      className="fixed-top p-3"
                      style={{
                        zIndex: 998,
                        background: "transparent",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "flex-start",
                        gap: "0.35em",
                        paddingRight: "1rem",
                      }}
                    >
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        aria-label="Close"
                        onClick={closeChordModal}
                        style={{
                          position: "absolute",
                          left: "10px",
                          top: "10px",
                        }}
                      />

                      {videoLoaded && selectedSong?.url && (
                        <div id="yt-iframe" className="youtube-iframe" style={{ display: showVideo ? "block" : "none" }}>
                          <iframe src={`https://www.youtube.com/embed/${selectedSong.track}?autoplay=1`} frameBorder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                        </div>
                      )}
                      <button
                        id="ytbutton"
                        onClick={() => {
                          setVideoLoaded(true); // Load video on first click
                          setShowVideo(!showVideo); // Toggle visibility
                        }}
                        className="btn btn-sm btn-light"
                      >
                        {showVideo ? "Hide YouTube" : "YouTube"}
                      </button>
                      <strong>Key: </strong>
                      <select className="btn btn-info btn-sm" value={chordKey} onChange={handleKeyChange}>
                        {["Ab", "A", "Bb", "B", "C", "D", "Eb", "E", "F", "G"].map((key) => (
                          <option key={key} value={key}>
                            {key}
                          </option>
                        ))}
                      </select>
                      <div className="list-group text-right" style={{ width: "50px" }}>
                        <button className="list-group-item list-group-item-action" onClick={() => handleTextSize(true)}>
                          +
                        </button>
                        <button className="list-group-item list-group-item-action" onClick={() => handleTextSize(false)}>
                          -
                        </button>
                      </div>
                      {isLeader === null ? (
                        <div className="text-center my-4 text-muted">
                          <small>Loading permissions...</small>
                        </div>
                      ) : isLeader ? (
                        <Button variant="warning" size="sm" onClick={() => openSongForm(selectedSong)}>
                          ✎
                        </Button>
                      ) : null}
                    </div>
                    <div className="chordpro-wrapper" style={{ position: "relative" }}>
                      <canvas
                        ref={canvasRef}
                        className={`notes-canvas ${isEditing ? "editing" : ""}`}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          zIndex: 999,
                          pointerEvents: isEditing ? "auto" : "none",
                          touchAction: isEditing ? "none" : "auto",
                        }}
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={endDraw}
                        onMouseLeave={endDraw}
                        onTouchStart={startDraw}
                        onTouchMove={draw}
                        onTouchEnd={endDraw}
                      />
                      <span className="order-circle" style={{ marginRight: "auto", marginLeft: "26px" }}>
                        {selectedSong?.order}
                      </span>
                      <div className="chordpro-content" dangerouslySetInnerHTML={{ __html: transposedHtml }} style={{ fontSize: `${fontSize}px`, padding: "20px" }} />
                    </div>

                    {!transposedHtml && <div className="chordpro-error">Error: No content rendered. Check console for details.</div>}
                    <div
                      className="fixed-bottom p-3"
                      style={{
                        zIndex: 1001,
                        background: "transparent",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <button className="btn btn-success btn-sm mr-2 startscroll" onClick={startScroll}>
                        ►
                      </button>
                      <button className="btn btn-danger btn-sm mr-2 stopscroll" onClick={stopScroll} style={{ display: "none" }}>
                        ■
                      </button>
                      {/* Notes controls */}
                      <button id="editNotesButton" className={`btn btn-${isEditing ? "warning" : "outline-light"} btn-sm mr-2`} onClick={toggleEditMode}>
                        {isEditing ? "✅" : "✏️"}
                      </button>

                      <button
                        id="clearNotesButton"
                        className="btn btn-outline-danger btn-sm mr-2"
                        onClick={() => {
                          if (!window.confirm("Clear saved notes for this song?")) return;
                          clearNotesForSong();
                        }}
                      >
                        🗑️
                      </button>

                      <button id="toggleNavButton" onClick={toggleNav} className="btn btn-light mr-2" aria-label="Toggle song navigation">
                        <h3 className="p-0">♫</h3>
                      </button>
                      <button id="nextbutton" className="btn btn-light" onClick={handleNextSong}>
                        <h3 className="p-0">⏩</h3>
                      </button>
                    </div>
                    <div className="nav nav-pills nav-fill bg-light fixed-bottom" style={{ display: "none" }} id="songnav">
                      {songs.map((song) => (
                        <li className="nav-item" key={song.id}>
                          <button
                            className="nav-link"
                            onClick={() => {
                              openChordModal(song);
                              toggleNav();
                            }}
                          >
                            {song.title}
                          </button>
                        </li>
                      ))}
                      <button className="btn btn-outline-primary" onClick={closeChordModal} aria-label="Close modal">
                        <h4 className="p-0">▤</h4>
                      </button>
                      <button className="btn btn-outline-primary" onClick={() => window.location.reload()} aria-label="Refresh page">
                        <h4 className="p-0">↺</h4>
                      </button>
                      <span
                        className="badge rounded-pill bg-danger text-white"
                        style={{
                          margin: "10px 5px",
                          padding: "8px 12px",
                          zIndex: 1002,
                        }}
                        onClick={toggleNav}
                        aria-label="Close navigation"
                      >
                        <strong>×</strong>
                      </span>
                    </div>
                  </div>
                )}
              </Modal.Body>
            </Modal>
            <Modal show={showSongForm} onHide={() => setShowSongForm(false)} size="lg">
              <Modal.Header closeButton className="d-flex justify-content-between align-items-center">
                <Modal.Title className="mb-0">{editingSong ? "Edit Song" : "Add New Song"}</Modal.Title>
                <Button variant="primary" size="sm" type="submit" form="editsongForm" style={{ fontWeight: "bold", marginLeft: "auto" }}>
                  💾 Save
                </Button>
              </Modal.Header>

              <Modal.Body>
                <Form onSubmit={handleSongSubmit} id={"editsongForm"}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={songFormData.title}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          title: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Key</Form.Label>
                    <Form.Select
                      value={songFormData.skey}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          skey: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">Select Key</option>
                      {["Ab", "A", "Bb", "B", "C", "D", "Eb", "E", "F", "G"].map((key) => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>YouTube URL (ID only, e.g., cOfXB-QYXMM)</Form.Label>
                    <Form.Control
                      type="text"
                      value={songFormData.url}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          url: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Spotify Link (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      value={songFormData.spotify}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          spotify: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Track YouTube ID (defaults to URL if blank)</Form.Label>
                    <Form.Control
                      type="text"
                      value={songFormData.track}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          track: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>ChordPro (lyrics and chords)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={15} // Large for memo-style input
                      value={songFormData.chordpro}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          chordpro: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Button variant="primary" type="submit">
                    💾 Save
                  </Button>
                </Form>
              </Modal.Body>
            </Modal>
          </div>
        )}
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}

export default App;
